import { useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import type { ThemeMode } from '../types';

/**
 * Applies the current theme class to <html> and provides a toggle function.
 * Call once near the app root (e.g. in App.tsx) so the class is always in sync.
 */
const THEME_STORAGE_KEY = 'theme';

export function useTheme() {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const theme: ThemeMode =
    user?.theme ?? (localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null) ?? 'light';

  // Sync the `dark` class on <html> whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const setTheme = useCallback(
    async (newTheme: ThemeMode) => {
      // Persist to localStorage immediately
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);

      // Optimistically update the UI
      updateUser({ theme: newTheme });

      try {
        await authService.updatePreferences({ theme: newTheme });
      } catch {
        // Revert on failure
        localStorage.setItem(THEME_STORAGE_KEY, theme);
        updateUser({ theme });
      }
    },
    [theme, updateUser],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme } as const;
}
